import { useId } from 'react';
import { cn } from '@/utils/cn';

interface ShowAllButtonGlowProps {
  className?: string;
}

export function ShowAllButtonGlow({ className }: ShowAllButtonGlowProps) {
  const rawId = useId();
  const safeId = rawId.replace(/:/g, '');

  // Shared outer clip (same shape in both states)
  const outerClipId = `show-all-outer-clip-${safeId}`;

  // Default state IDs
  const dInnerClipId = `show-all-d-inner-clip-${safeId}`;
  const dFilter0Id = `show-all-d-filter0-${safeId}`;
  const dFilter1Id = `show-all-d-filter1-${safeId}`;
  const dFilter2Id = `show-all-d-filter2-${safeId}`;
  const dFilter3Id = `show-all-d-filter3-${safeId}`;
  const dPaint0Id = `show-all-d-paint0-${safeId}`;
  const dPaint1Id = `show-all-d-paint1-${safeId}`;
  const dPaint2Id = `show-all-d-paint2-${safeId}`;
  const dPaint3Id = `show-all-d-paint3-${safeId}`;

  // Hover state IDs
  const hInnerClipId = `show-all-h-inner-clip-${safeId}`;
  const hFilter0Id = `show-all-h-filter0-${safeId}`;
  const hFilter1Id = `show-all-h-filter1-${safeId}`;
  const hFilter2Id = `show-all-h-filter2-${safeId}`;
  const hPaint0Id = `show-all-h-paint0-${safeId}`;
  const hPaint1Id = `show-all-h-paint1-${safeId}`;
  const hPaint2Id = `show-all-h-paint2-${safeId}`;

  return (
    <div className={cn('group relative h-full w-full', className)}>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 120 160"
        preserveAspectRatio="none"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* ── Default state (fades out on hover) ── */}
        <g clipPath={`url(#${outerClipId})`}>
          <g
            clipPath={`url(#${dInnerClipId})`}
            className="transition-opacity duration-300 group-hover:opacity-0"
          >
            <rect y="-54.6528" width="120" height="269.306" rx="16" fill="#1C0F29" />
            <g opacity="0.65" filter={`url(#${dFilter0Id})`}>
              <path
                d="M1.05266 188.2C0.471254 173.591 38.1206 154.362 85.1448 145.252C132.169 136.141 170.761 140.599 171.342 155.209C171.924 169.818 134.274 189.047 87.2503 198.157C40.2261 207.267 1.63407 202.81 1.05266 188.2Z"
                fill={`url(#${dPaint0Id})`}
              />
            </g>
            <g opacity="0.3" filter={`url(#${dFilter1Id})`}>
              <ellipse
                cx="85.5466"
                cy="47.4794"
                rx="85.5466"
                ry="47.4794"
                transform="matrix(-0.445062 -0.8955 0.381973 -0.924174 110.173 186.32)"
                fill={`url(#${dPaint1Id})`}
              />
            </g>
            <g opacity="0.8" filter={`url(#${dFilter2Id})`}>
              <ellipse
                cx="53.8328"
                cy="7.62089"
                rx="53.8328"
                ry="7.62089"
                transform="matrix(-0.0421617 -0.999111 0.979544 -0.201231 132.166 193.236)"
                fill={`url(#${dPaint2Id})`}
              />
            </g>
            <g opacity="0.5" filter={`url(#${dFilter3Id})`}>
              <ellipse
                cx="72.0298"
                cy="7.80845"
                rx="72.0298"
                ry="7.80845"
                transform="matrix(-0.862985 -0.505229 0.119399 -0.992846 158.275 178.849)"
                fill={`url(#${dPaint3Id})`}
              />
            </g>
          </g>
        </g>

        {/* ── Hover state (fades in on hover) ── */}
        <g clipPath={`url(#${outerClipId})`}>
          <g
            clipPath={`url(#${hInnerClipId})`}
            className="opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          >
            <rect y="-72.822" width="120" height="305.644" rx="16" fill="#1C0F29" />
            <g opacity="0.65" filter={`url(#${hFilter0Id})`}>
              <path
                d="M24.1424 173.457C10.8089 140.671 31.592 95.2476 70.5626 72.0016C109.533 48.7556 151.934 56.4896 165.268 89.2759C178.601 122.062 157.818 167.485 118.848 190.731C79.8769 213.977 37.4759 206.243 24.1424 173.457Z"
                fill={`url(#${hPaint0Id})`}
              />
            </g>
            <g filter={`url(#${hFilter1Id})`}>
              <ellipse
                cx="2.69286"
                cy="15.7147"
                rx="2.69286"
                ry="15.7147"
                transform="matrix(0.999246 -0.0388393 0.007984 0.999968 94.42 -11.1625)"
                fill={`url(#${hPaint1Id})`}
              />
            </g>
          </g>
          <g
            opacity="0.3"
            filter={`url(#${hFilter2Id})`}
            className="opacity-0 transition-opacity duration-300 group-hover:opacity-30"
          >
            <ellipse
              cx="85.5466"
              cy="47.4794"
              rx="85.5466"
              ry="47.4794"
              transform="matrix(-0.445062 -0.8955 0.381973 -0.924174 23.1472 146.972)"
              fill={`url(#${hPaint2Id})`}
            />
          </g>
        </g>

        <defs>
          {/* Shared outer clip */}
          <clipPath id={outerClipId}>
            <path
              d="M0 12C0 5.37258 5.37258 0 12 0H108C114.627 0 120 5.37258 120 12V148C120 154.627 114.627 160 108 160H12C5.37259 160 0 154.627 0 148V12Z"
              fill="white"
            />
          </clipPath>

          {/* Default state defs */}
          <clipPath id={dInnerClipId}>
            <rect y="-54.6528" width="120" height="269.306" rx="16" fill="white" />
          </clipPath>
          <filter id={dFilter0Id} x="-63.954" y="75.5256" width="300.303" height="192.358" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="32.5" result="effect1_foregroundBlur" />
          </filter>
          <filter id={dFilter1Id} x="8.05347" y="-62.4654" width="164.364" height="256.597" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="20" result="effect1_foregroundBlur" />
          </filter>
          <filter id={dFilter2Id} x="89.5564" y="44.1097" width="95.6089" height="187.615" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="20" result="effect1_foregroundBlur" />
          </filter>
          <filter id={dFilter3Id} x="9.87878" y="72.4894" width="174.336" height="124.432" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="12.5" result="effect1_foregroundBlur" />
          </filter>
          <linearGradient id={dPaint0Id} x1="123.552" y1="186.758" x2="75.4193" y2="137.838" gradientUnits="userSpaceOnUse">
            <stop stopColor="#4C2959" />
            <stop offset="0.365293" stopColor="#BD4DF9" />
            <stop offset="1" stopColor="#FE5104" />
          </linearGradient>
          <linearGradient id={dPaint1Id} x1="37.3914" y1="-6.17593" x2="227.444" y2="35.2223" gradientUnits="userSpaceOnUse">
            <stop offset="0.138301" stopColor="#42316D" />
            <stop offset="0.442308" stopColor="#6F3496" />
            <stop offset="1" stopColor="#6433BD" />
          </linearGradient>
          <linearGradient id={dPaint2Id} x1="23.5297" y1="-0.991296" x2="95.9701" y2="60.872" gradientUnits="userSpaceOnUse">
            <stop offset="0.0739286" stopColor="#A644E6" />
            <stop offset="1" stopColor="#6433BD" />
          </linearGradient>
          <linearGradient id={dPaint3Id} x1="75.4357" y1="14.4089" x2="75.2269" y2="-0.0689577" gradientUnits="userSpaceOnUse">
            <stop offset="0.138301" stopColor="#42316D" />
            <stop offset="0.442308" stopColor="#6F3496" />
            <stop offset="1" stopColor="#6433BD" />
          </linearGradient>

          {/* Hover state defs */}
          <clipPath id={hInnerClipId}>
            <rect y="-72.822" width="120" height="305.644" rx="16" fill="white" />
          </clipPath>
          <filter id={hFilter0Id} x="-44.8936" y="-6.41168" width="279.197" height="275.556" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="32.5" result="effect1_foregroundBlur" />
          </filter>
          <filter id={hFilter1Id} x="54.5425" y="-51.2675" width="85.3876" height="111.429" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="20" result="effect1_foregroundBlur" />
          </filter>
          <filter id={hFilter2Id} x="-78.9727" y="-101.813" width="164.364" height="256.597" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="20" result="effect1_foregroundBlur" />
          </filter>
          <linearGradient id={hPaint0Id} x1="145.121" y1="162.978" x2="48.1941" y2="157.043" gradientUnits="userSpaceOnUse">
            <stop stopColor="#4C2959" />
            <stop offset="0.365293" stopColor="#BD4DF9" />
            <stop offset="1" stopColor="#FE5104" />
          </linearGradient>
          <linearGradient id={hPaint1Id} x1="5.49437" y1="3.1894" x2="7.6493" y2="3.46562" gradientUnits="userSpaceOnUse">
            <stop offset="0.00480769" stopColor="#3C4CFF" />
            <stop offset="0.838771" stopColor="#5C0BEE" />
          </linearGradient>
          <linearGradient id={hPaint2Id} x1="37.3914" y1="-6.17593" x2="227.444" y2="35.2223" gradientUnits="userSpaceOnUse">
            <stop offset="0.138301" stopColor="#42316D" />
            <stop offset="0.442308" stopColor="#6F3496" />
            <stop offset="1" stopColor="#6433BD" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}
